import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getRelativeRegisterFormListApi } from "../../api/finance.collaborator.api";
import { lockingStatusUi } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const LIMIT = 10;
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ", sortBy: "createdAt", sortOrder: "desc" },
  { key: "OLDEST", label: "Cũ đến mới", sortBy: "createdAt", sortOrder: "asc" },
];
const STATUS_OPTIONS = [
  { value: "ALL", label: "Tất cả" },
  { value: "NEW", label: "Đang chờ duyệt" },
  { value: "APPROVED", label: "Đã duyệt" },
  { value: "REJECTED", label: "Từ chối" },
];

const genderText = (gender) => {
  if (Number(gender) === 0) return "Nam";
  if (Number(gender) === 1) return "Nữ";
  if (Number(gender) === 2) return "Khác";
  return "-";
};

const Row = ({ label, value }) => (
  <Flex gap="12px" align="flex-start" mb="8px">
    <Text fontSize="14px" color={MUTED} flex="0 0 42%">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={TEXT} flex="1" textAlign="right" noOfLines={3}>{value || "-"}</Text>
  </Flex>
);

const RequestCard = ({ item, onClick }) => {
  const ui = lockingStatusUi(item?.status);
  const r = item?.relativeInfo || {};
  return (
    <Box as="button" type="button" onClick={onClick} textAlign="left" w="100%" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
      <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={2}>{r?.fullName || "-"}{r?.code ? " - " + r.code : ""}</Text>
      <Flex align="center" gap="8px" mt="8px" flexWrap="wrap">
        <Box bg={ui.bg} borderRadius="6px" px="8px" py="3px">
          <Text fontSize="11px" fontWeight={600} color={ui.color}>{ui.label}</Text>
        </Box>
      </Flex>
      <Box h="1px" bg={BORDER} my="12px" />
      <Row label="Email" value={r?.email} />
      <Row label="Số điện thoại" value={r?.phone} />
      <Row label="CCCD" value={r?.identificationNumber} />
      <Row label="Ngày sinh" value={r?.dateOfBirth ? fmtD(r.dateOfBirth) : "-"} />
      <Row label="Giới tính" value={genderText(r?.gender)} />
      <Row label="Mã số thuế" value={r?.taxCode} />
    </Box>
  );
};

const CollaboratorRequestList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [filterOpen, setFilterOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const sheet = useDisclosure();
  const [sheetType, setSheetType] = useState(null);
  const reqId = useRef(0);
  const searchTimer = useRef(null);

  const sortOption = SORT_OPTIONS.find((x) => x.key === sortKey) || SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      status: status !== "ALL" ? status : undefined,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
      search: search || undefined,
    }),
    [status, sortOption, search]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getRelativeRegisterFormListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || data || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setTotal(data?.pagination?.total ?? list.length);
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sortKey, search]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v.trim()), 300);
  };

  const openSheet = (t) => { setSheetType(t); sheet.onOpen(); };
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === status)?.label || "Tất cả";
  const sheetOptions = sheetType === "STATUS"
    ? STATUS_OPTIONS.map((s) => ({ key: s.value, label: s.label, raw: s.value }))
    : SORT_OPTIONS.map((s) => ({ key: s.key, label: s.label, raw: s.key }));
  const isActive = (raw) => (sheetType === "STATUS" ? raw === status : raw === sortKey);
  const pickItem = (raw) => { if (sheetType === "STATUS") setStatus(raw); else setSortKey(raw); sheet.onClose(); };

  const extras = (
    <>
      <Box as="button" display={{ base: "block", md: "none" }} position="fixed" right={{ base: "20px", md: "calc(50% - 220px)" }} bottom="24px" zIndex={15} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu/tao`)}>
        <img src={appImages.icon_create} alt="Tạo mới" width={64} height={64} style={{ display: "block" }} />
      </Box>

      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">{sheetType === "STATUS" ? "Trạng thái" : "Sắp xếp"}</Text>
            <Box maxH="50vh" overflowY="auto">
              {sheetOptions.map((it, i) => {
                const active = isActive(it.raw);
                const last = i === sheetOptions.length - 1;
                return (
                  <Flex key={String(it.key)} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={last ? "none" : "1px solid #EEF1F6"} onClick={() => pickItem(it.raw)}>
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400} textAlign="left">{it.label}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => {
      const r = it?.relativeInfo || {};
      return {
        id: it._id,
        ui: lockingStatusUi(it?.status),
        name: `${r?.fullName || "-"}${r?.code ? " - " + r.code : ""}`,
        email: r?.email || "-",
        phone: r?.phone || "-",
        identification: r?.identificationNumber || "-",
        birth: r?.dateOfBirth ? fmtD(r.dateOfBirth) : "-",
        gender: genderText(r?.gender),
        taxCode: r?.taxCode || "-",
      };
    });

    return (
      <DesktopPage
        title="Phiếu đăng ký CTV"
        right={
          <DesktopButton
            onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu/tao`)}
          >
            + Tạo phiếu đăng ký
          </DesktopButton>
        }
      >
        <DesktopStatRow items={[{ key: "total", title: "Số lượng phiếu", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <Box w="320px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Tìm kiếm
              </Text>
              <DesktopSearch placeholder="Nhập tên CTV ..." onChange={onSearchChange} maxW="320px" />
            </Box>
            <Box w="200px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Trạng thái
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("STATUS")}
              >
                {statusLabel}
              </Box>
            </Box>
            <Box w="200px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có phiếu"
            onRowClick={(row) =>
              navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu/${row.id}`)
            }
            rows={rows}
            headers={[
              { key: "name", label: "Cộng tác viên", align: "left", width: "20%" },
              { key: "phone", label: "Số điện thoại", width: "12%" },
              { key: "email", label: "Email", align: "left", width: "16%" },
              { key: "identification", label: "CCCD", width: "12%" },
              { key: "birth", label: "Ngày sinh", width: "10%" },
              { key: "gender", label: "Giới tính", width: "8%" },
              { key: "taxCode", label: "Mã số thuế", width: "10%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "12%",
                render: (row) => (
                  <Box display="inline-block" bg={row.ui.bg} borderRadius="6px" px="10px" py="3px">
                    <Text fontSize="12px" fontWeight={600} color={row.ui.color}>
                      {row.ui.label}
                    </Text>
                  </Box>
                ),
              },
            ]}
          />

          {hasNext && (
            <Flex
              as="button"
              onClick={() => fetchPage(page + 1, "append")}
              justify="center"
              py="16px"
              w="100%"
              color={APP.primary200}
              fontWeight={600}
              fontSize="14px"
            >
              {loading ? <Spinner size="sm" /> : "Xem thêm"}
            </Flex>
          )}
        </DesktopPanel>

        {extras}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="90px">
      <AppBar title="Phiếu đăng ký CTV" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien`)} />

      <Box px="16px" pt="12px">
        <Flex h="44px" align="center" px="12px" bg="white" borderTop="1px solid" borderLeft="1px solid" borderRight="1px solid" borderColor={BORDER} borderTopRadius="12px">
          <AppIcon src={appImages.icon_search} size={20} color="#94A3B8" />
          <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Nhập tên CTV ..." onChange={onSearchChange} />
        </Flex>

        <Flex as="button" w="100%" h="44px" px="12px" align="center" justify="space-between" bg="white" borderTop="1px solid" borderLeft="1px solid" borderRight="1px solid" borderBottom={filterOpen ? "0" : "1px solid"} borderColor={BORDER} borderBottomRadius={filterOpen ? "0" : "12px"} onClick={() => setFilterOpen((p) => !p)}>
          <Flex align="center" gap="8px">
            <img src={appImages.icon_filter_detail} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
            <Text fontSize="14px" fontWeight={600} color={TEXT}>Bộ lọc chi tiết</Text>
          </Flex>
          <img src={filterOpen ? appImages.icon_chevron_up : appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
        </Flex>
        <Collapse in={filterOpen} animateOpacity>
          <Box bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius="12px" p="12px">
            <Box>
              <Text fontSize="13px" color={TEXT} mb="6px">Trạng thái</Text>
              <Flex as="button" type="button" onClick={() => openSheet("STATUS")} align="center" justify="space-between" bg="#F7F9FC" borderRadius="8px" h="40px" px="10px" w="100%">
                <Text fontSize="14px" color={TEXT}>{statusLabel}</Text>
                <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
              </Flex>
            </Box>
          </Box>
        </Collapse>

        <Box mt="12px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
          <Text fontSize="13px" color={MUTED} fontWeight={500}>SỐ LƯỢNG PHIẾU</Text>
          <Text fontSize="22px" fontWeight={800} color={APP.primary200} mt="8px">{total || 0}</Text>
        </Box>

        <Flex mt="16px" mb="14px" align="center" justify="space-between">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Danh sách phiếu</Text>
          <Flex as="button" align="center" gap="6px" onClick={() => openSheet("SORT")}>
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{sortOption.label}</Text>
            <img src={appImages.ion_sort_desc} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
          </Flex>
        </Flex>

        {loading && !items.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Không có phiếu</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <RequestCard key={it._id} item={it} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu/${it._id}`)} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      {/* FAB tạo phiếu đăng ký CTV mới */}
      {extras}
    </Box>
  );
};

export default CollaboratorRequestList;
